import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DataTable from "react-data-table-component";
import { Box, MenuItem, TextField, Typography } from "@mui/material";

import DashboardMenu from "../../commonComponents/DashboardMenu";
import useStyles from "../DashboardAuction/dashAuction.style";
import { getUserData } from "../../../common";
import {
  JoinRequestChip,
  joinRequestDetail,
  joinRequestState,
  requestsTabPath,
} from "../../commonComponents/JoinRequestStatus";
import { getAllJoinRequestsAPI } from "../../../api/joinRequestsAPI";
import getOrganiserDirectoryAPI from "../../../api/getOrganiserDirectoryAPI";

const STATUSES = [
  { value: "PENDING", label: "Waiting" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Not accepted" },
  { value: "WITHDRAWN", label: "Withdrawn" },
  { value: "CLOSED", label: "Closed" },
  { value: "", label: "All" },
];

const PAGE_SIZE = 25;

// Admin: every request to participate, across organisers. View only - "Open"
// goes to the auction's Requests tab, where the admin decides like an
// organiser.
const JoinRequestsAdmin = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isAdmin = (getUserData() ?? {}).role === "ADMIN";

  const [filters, setFilters] = useState({
    status: "PENDING",
    organiserId: "",
    search: "",
    from: "",
  });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ count: 0, requests: [] });
  const [loading, setLoading] = useState(true);
  const [organisers, setOrganisers] = useState([]);

  useEffect(() => {
    getOrganiserDirectoryAPI()
      .then(({ data }) => setOrganisers(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setOrganisers([]));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    const params = { page, pageSize: PAGE_SIZE };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = key === "from" ? moment(value).toISOString() : value;
    });
    // typing in the search box waits a moment before asking the server
    const timer = setTimeout(() => {
      getAllJoinRequestsAPI(params)
        .then(({ data }) => setResult(data))
        .catch(() => setResult({ count: 0, requests: [] }))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, page, isAdmin]);

  const setFilter = (key) => (event) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: event.target.value }));
  };

  const columns = [
    {
      name: "Auction",
      grow: 2,
      cell: (row) => <span style={{ fontWeight: 500 }}>{row.auctionTitle}</span>,
    },
    { name: "Organiser", selector: (row) => row.organiserName || "" },
    {
      name: "Bidder",
      cell: (row) =>
        [row.user?.firstName, row.user?.lastName].filter(Boolean).join(" "),
    },
    { name: "Company", selector: (row) => row.companyName || "" },
    {
      name: "Requested",
      selector: (row) => row.createdAt,
      format: (row) => moment(row.createdAt).format("D MMM YYYY"),
    },
    {
      name: "Status",
      grow: 2,
      cell: (row) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", py: 1 }}>
          <JoinRequestChip state={joinRequestState(row)} />
          {row.waitingDays !== null && row.waitingDays !== undefined && (
            <span style={{ color: row.waitingDays >= 2 ? "#B4321A" : "#4B5563" }}>
              {row.waitingDays < 1
                ? "today"
                : `${row.waitingDays} ${row.waitingDays === 1 ? "day" : "days"}`}
            </span>
          )}
          <span style={{ color: "#4B5563" }}>{joinRequestDetail(row)}</span>
        </Box>
      ),
    },
    {
      name: "",
      cell: (row) => (
        <a
          href={`#${requestsTabPath(row.eventId)}`}
          onClick={(event) => {
            event.preventDefault();
            navigate(requestsTabPath(row.eventId));
          }}
        >
          Open
        </a>
      ),
    },
  ];

  return (
    <div className={classes.section}>
      <div className="container">
        <div className={classes.Dashboard}>
          <div className={classes.dashboardCard}>
            <DashboardMenu />
            <div className={classes.dashboardCards}>
              <Typography variant="h5" sx={{ fontWeight: 700, my: 2 }}>
                All join requests
              </Typography>
              {!isAdmin ? (
                <Typography>Only admins can see this page.</Typography>
              ) : (
                <>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <TextField
                      select
                      size="small"
                      label="Status"
                      value={filters.status}
                      onChange={setFilter("status")}
                      sx={{ minWidth: 160 }}
                    >
                      {STATUSES.map((status) => (
                        <MenuItem key={status.label} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Organiser"
                      value={filters.organiserId}
                      onChange={setFilter("organiserId")}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">All organisers</MenuItem>
                      {organisers.map((organiser) => (
                        <MenuItem key={organiser.userId} value={organiser.userId}>
                          {organiser.companyName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Auction"
                      placeholder="Search auction"
                      value={filters.search}
                      onChange={setFilter("search")}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Requested from"
                      InputLabelProps={{ shrink: true }}
                      value={filters.from}
                      onChange={setFilter("from")}
                    />
                  </Box>
                  <DataTable
                    columns={columns}
                    data={result.requests || []}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={result.count || 0}
                    paginationPerPage={PAGE_SIZE}
                    paginationRowsPerPageOptions={[PAGE_SIZE]}
                    onChangePage={setPage}
                    responsive
                    noDataComponent={<Box sx={{ p: 3, color: "#4B5563" }}>No requests.</Box>}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinRequestsAdmin;
